import { createAsyncThunk } from '@reduxjs/toolkit';
import { enqueueSnackbar } from 'notistack';
import { getFormattedErr } from 'src/helpers';
import apiClient from 'src/helpers/apiClient';
import type {
  CreateDailyMissionBody,
  CreateRewardPromotionBody,
  CreateUserLevelBody,
  UpdateDailyMissionBody,
  UpdateRewardPromotionBody,
  UpdateUserLevelBody,
} from 'src/openapi';

/**
 * Admin CRUD for the three reward tables: levels, missions and promotions.
 *
 * All three are rows rather than constants so a new level, mission or
 * double-rewards weekend is an admin action instead of a deploy — these are the
 * endpoints that make that true. Kept apart from the learner-facing thunks in
 * `rewards.ts`, which read the same data but never write it.
 */

const notifyError = (err: any) => {
  enqueueSnackbar(getFormattedErr(err?.response?.data?.error), {
    variant: 'error',
  });
};

/* ── platform levels ────────────────────────────────────────────────────── */

export const getAllUserLevels = createAsyncThunk(
  'rewardsAdmin/getAllUserLevels',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const api = await apiClient.getUserLevelApi();
      const response = await api.userLevelApiGetAllUserLevels({ signal });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getUserLevelById = createAsyncThunk(
  'rewardsAdmin/getUserLevelById',
  async ({ levelId }: { levelId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getUserLevelApi();
      const response = await api.userLevelApiGetUserLevelById({ levelId });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createUserLevel = createAsyncThunk(
  'rewardsAdmin/createUserLevel',
  async ({ body }: { body: CreateUserLevelBody }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getUserLevelApi();
      const response = await api.userLevelApiCreateUserLevel({
        createUserLevelBody: body,
      });
      enqueueSnackbar('Level created', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateUserLevel = createAsyncThunk(
  'rewardsAdmin/updateUserLevel',
  async (
    { levelId, body }: { levelId: string; body: UpdateUserLevelBody },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getUserLevelApi();
      const response = await api.userLevelApiUpdateUserLevel({
        levelId,
        updateUserLevelBody: body,
      });
      enqueueSnackbar('Level updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteUserLevel = createAsyncThunk(
  'rewardsAdmin/deleteUserLevel',
  async ({ levelId }: { levelId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getUserLevelApi();
      const response = await api.userLevelApiDeleteUserLevel({ levelId });
      // Deactivated, not removed: a learner already paid for this rung has a
      // vault row pointing at it.
      enqueueSnackbar('Level deactivated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

/* ── daily missions ─────────────────────────────────────────────────────── */

export const getAllDailyMissions = createAsyncThunk(
  'rewardsAdmin/getAllDailyMissions',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const api = await apiClient.getDailyMissionApi();
      const response = await api.dailyMissionApiGetAllDailyMissions({ signal });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getDailyMissionById = createAsyncThunk(
  'rewardsAdmin/getDailyMissionById',
  async ({ missionId }: { missionId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getDailyMissionApi();
      const response = await api.dailyMissionApiGetDailyMissionById({
        missionId,
      });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createDailyMission = createAsyncThunk(
  'rewardsAdmin/createDailyMission',
  async ({ body }: { body: CreateDailyMissionBody }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getDailyMissionApi();
      const response = await api.dailyMissionApiCreateDailyMission({
        createDailyMissionBody: body,
      });
      enqueueSnackbar('Mission created', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateDailyMission = createAsyncThunk(
  'rewardsAdmin/updateDailyMission',
  async (
    { missionId, body }: { missionId: string; body: UpdateDailyMissionBody },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getDailyMissionApi();
      const response = await api.dailyMissionApiUpdateDailyMission({
        missionId,
        updateDailyMissionBody: body,
      });
      enqueueSnackbar('Mission updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteDailyMission = createAsyncThunk(
  'rewardsAdmin/deleteDailyMission',
  async ({ missionId }: { missionId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getDailyMissionApi();
      const response = await api.dailyMissionApiDeleteDailyMission({
        missionId,
      });
      enqueueSnackbar('Mission deactivated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

/* ── reward promotions ──────────────────────────────────────────────────── */

export const getAllRewardPromotions = createAsyncThunk(
  'rewardsAdmin/getAllRewardPromotions',
  async (_: void, { rejectWithValue, signal }) => {
    try {
      const api = await apiClient.getRewardPromotionApi();
      const response = await api.rewardPromotionApiGetAllRewardPromotions({
        signal,
      });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getRewardPromotionById = createAsyncThunk(
  'rewardsAdmin/getRewardPromotionById',
  async ({ promotionId }: { promotionId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getRewardPromotionApi();
      const response = await api.rewardPromotionApiGetRewardPromotionById({
        promotionId,
      });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const createRewardPromotion = createAsyncThunk(
  'rewardsAdmin/createRewardPromotion',
  async (
    { body }: { body: CreateRewardPromotionBody },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getRewardPromotionApi();
      const response = await api.rewardPromotionApiCreateRewardPromotion({
        createRewardPromotionBody: body,
      });
      enqueueSnackbar('Promotion created', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const updateRewardPromotion = createAsyncThunk(
  'rewardsAdmin/updateRewardPromotion',
  async (
    {
      promotionId,
      body,
    }: { promotionId: string; body: UpdateRewardPromotionBody },
    { rejectWithValue },
  ) => {
    try {
      const api = await apiClient.getRewardPromotionApi();
      const response = await api.rewardPromotionApiUpdateRewardPromotion({
        promotionId,
        updateRewardPromotionBody: body,
      });
      enqueueSnackbar('Promotion updated', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);

export const deleteRewardPromotion = createAsyncThunk(
  'rewardsAdmin/deleteRewardPromotion',
  async ({ promotionId }: { promotionId: string }, { rejectWithValue }) => {
    try {
      const api = await apiClient.getRewardPromotionApi();
      const response = await api.rewardPromotionApiDeleteRewardPromotion({
        promotionId,
      });
      // Disabled, not removed: the window it ran between is what explains vault
      // rows that paid more than the ladder now says.
      enqueueSnackbar('Promotion disabled', { variant: 'success' });
      return response.data;
    } catch (err: any) {
      notifyError(err);
      return rejectWithValue(err?.response?.data);
    }
  },
);
